import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { ChargeItem, Encounter, EpisodeOfCare } from '@medplum/fhirtypes';
import { useMedplumSubscriptionRefresh } from '@/hooks/useMedplumSubscriptionRefresh';
import { buildEncounterByIdSubscriptionCriteria } from '@/lib/fhirRealtimeCriteria';

export interface AppointmentNoteSources {
  encounter: Encounter | null;
  chargeItems: ChargeItem[];
  episodeOfCare: EpisodeOfCare | null;
  loading: boolean;
  /** Re-read Encounter + related billing sources (e.g. after Zoom webhook updates). */
  refetch: () => void;
}

export function useAppointmentNoteSources(
  encounterId: string | undefined,
  episodeOfCareId: string | undefined,
): AppointmentNoteSources {
  const medplum = useMedplum();
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [episodeOfCare, setEpisodeOfCare] = useState<EpisodeOfCare | null>(null);
  const [loading, setLoading] = useState(false);
  const loadGenerationRef = useRef(0);

  const loadNoteSources = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!encounterId) {
        setEncounter(null);
        setChargeItems([]);
        setEpisodeOfCare(null);
        setLoading(false);
        return;
      }

      const generation = ++loadGenerationRef.current;
      if (!options?.silent) {
        setLoading(true);
      }

      try {
        medplum.invalidateSearches('Encounter');
        medplum.invalidateSearches('ChargeItem');
        const enc = await medplum.readResource('Encounter', encounterId);
        if (generation !== loadGenerationRef.current) return;
        setEncounter(enc);

        const eocRef = episodeOfCareId ?? enc.episodeOfCare?.[0]?.reference?.replace('EpisodeOfCare/', '');
        if (eocRef) {
          try {
            const eoc = await medplum.readResource('EpisodeOfCare', eocRef);
            if (generation !== loadGenerationRef.current) return;
            setEpisodeOfCare(eoc);
          } catch {
            if (generation !== loadGenerationRef.current) return;
            setEpisodeOfCare(null);
          }
        } else if (generation === loadGenerationRef.current) {
          setEpisodeOfCare(null);
        }

        try {
          medplum.invalidateSearches('ChargeItem');
          const charges = await medplum.searchResources('ChargeItem', {
            context: `Encounter/${encounterId}`,
            _count: '10',
          });
          if (generation === loadGenerationRef.current) {
            setChargeItems(charges);
          }
        } catch {
          if (generation === loadGenerationRef.current) {
            setChargeItems([]);
          }
        }
      } catch {
        if (generation === loadGenerationRef.current) {
          setEncounter(null);
          setChargeItems([]);
        }
      } finally {
        if (generation === loadGenerationRef.current && !options?.silent) {
          setLoading(false);
        }
      }
    },
    [medplum, encounterId, episodeOfCareId],
  );

  const refetch = useCallback(() => void loadNoteSources({ silent: true }), [loadNoteSources]);

  useEffect(() => {
    void loadNoteSources();
  }, [loadNoteSources]);

  const encounterSubscriptionCriteria = useMemo(
    () => (encounterId ? buildEncounterByIdSubscriptionCriteria(encounterId) : undefined),
    [encounterId],
  );

  useMedplumSubscriptionRefresh(encounterSubscriptionCriteria, refetch, {
    enabled: Boolean(encounterId),
    debugLabel: '[AppointmentNoteSources][WS]',
  });

  return {
    encounter,
    chargeItems,
    episodeOfCare,
    loading,
    refetch,
  };
}
